import type { AvailableLanguage, TextChanges, Translations } from '../api/business/types';
import type { Language } from '../i18n/Language';

/** One text of a row by language, '' where it is missing — what a form edits. */
export const textsOf = <TTexts extends Record<string, string | null>>(
  translations: Translations<TTexts> | undefined,
  field: keyof TTexts,
  languages: Array<string>,
): Record<string, string> =>
  Object.fromEntries(languages.map((code) => [code, translations?.[code]?.[field] ?? '']));

/** The content languages a text has not been written in, in their order. */
export const missingLanguages = (texts: Record<string, string>, languages: Array<string>): Array<string> =>
  languages.filter((code) => (texts[code] ?? '').trim() === '');

/**
 * What a form sends: on a create every language with a text; on an update only what changed, an emptied
 * language as null — which clears it. The API refuses to clear the default language's.
 */
export const textChanges = (
  texts: Record<string, string>,
  original: Record<string, string> | null,
  languages: Array<string>,
): TextChanges => {
  const changes: TextChanges = {};

  for (const code of languages) {
    const text = (texts[code] ?? '').trim();

    if (original === null) {
      if (text !== '') changes[code] = text;
    } else if (text !== (original[code] ?? '').trim()) {
      changes[code] = text === '' ? null : text;
    }
  }

  return changes;
};

/** A language's name in the back office's language; the code when the list does not know it. */
export const languageName = (code: string, available: Array<AvailableLanguage>, language: Language): string =>
  available.find((one) => one.code === code)?.names[language] ?? code.toUpperCase();
